export const BRAND_MARK = "/artwork/wecrew-mark.jpg";

export function WeCrewMark({ className = "" }) {
  return <svg className={className} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <path d="M12 17 22 47 32 26 42 47 52 17" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M12 17 32 6 52 17M22 47l10 11 10-11" stroke="currentColor" strokeWidth="1.3"/>
  </svg>;
}

export function BrandArt({ className = "", alt = "", fetchPriority, loading = "lazy" }) {
  return <img
    className={className}
    src={BRAND_MARK}
    alt={alt}
    width="1024"
    height="541"
    decoding="async"
    loading={loading}
    {...(fetchPriority ? { fetchPriority } : {})}
  />;
}

export function PublicBrand({ onClick }) {
  return <button type="button" className="wc-wordmark" onClick={onClick} aria-label="WeCrew home">
    <span className="wc-wordmark-mark" aria-hidden="true">✳</span>
    WeCrew
  </button>;
}

function PhoneScreen({ kicker, question, reply, source }) {
  return <div className="wc-phone-screen">
    <div className="wc-orb" aria-hidden="true"/>
    <p className="wc-phone-kicker">{kicker}</p>
    <p className="wc-example-question">{question}</p>
    <p className="wc-phone-reply">{reply}</p>
    {source && <span className="wc-source-chip">{source}</span>}
  </div>;
}

export function PhoneMock({ kicker = "Ask", question, reply, source, compact = false, className = "" }) {
  return <div className={`wc-phone ${compact ? "is-compact" : ""} ${className}`.trim()}>
    <div className="wc-phone-notch" aria-hidden="true"/>
    <PhoneScreen kicker={kicker} question={question} reply={reply} source={source}/>
  </div>;
}

function HandSilhouette() {
  return <>
    <svg className="wc-hand wc-hand-back" viewBox="0 0 480 600" aria-hidden="true">
      <defs>
        <linearGradient id="wc-hand-fade" x1="250" y1="300" x2="250" y2="600">
          <stop offset="0%" stopColor="#0e1116"/>
          <stop offset="40%" stopColor="#0e1116"/>
          <stop offset="100%" stopColor="#0e1116" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <g fill="url(#wc-hand-fade)">
        <rect x="186" y="330" width="132" height="250" rx="66"/>
        <ellipse cx="258" cy="312" rx="96" ry="78"/>
        <rect x="292" y="96" width="50" height="168" rx="25" transform="rotate(7 317 180)"/>
        <rect x="318" y="118" width="48" height="164" rx="24" transform="rotate(13 342 200)"/>
        <rect x="338" y="150" width="44" height="146" rx="22" transform="rotate(18 360 223)"/>
        <rect x="350" y="188" width="40" height="118" rx="20" transform="rotate(24 370 247)"/>
      </g>
    </svg>
    <svg className="wc-hand wc-hand-front" viewBox="0 0 480 600" aria-hidden="true">
      <rect fill="#0e1116" x="28" y="228" width="50" height="122" rx="25" transform="rotate(-16 53 289)"/>
    </svg>
  </>;
}

export function HeroDevice() {
  return <div className="wc-hero-stage" aria-hidden="true">
    <div className="wc-hand-scene">
      <HandSilhouette/>
      <PhoneMock
        question="Which checks come before a release?"
        reply="Start with the release checklist, then confirm rollback owner and the customer notice window."
        source="Release playbook"
      />
    </div>
  </div>;
}

export function PhoneCluster() {
  return <div className="wc-phone-cluster" aria-hidden="true">
    <PhoneMock compact className="is-left" kicker="Ask" question="Where is the rollback owner listed?" reply="In the release playbook, under change control." source="Playbook §4"/>
    <PhoneMock compact className="is-center" kicker="Ask" question="Which checks come before a release?" reply="Checklist, rollback owner, then customer notice timing." source="3 sources"/>
    <PhoneMock compact className="is-right" kicker="Library" question="Indexed this week" reply="Release playbook.pdf and the support handbook are ready to ask." source="2 files"/>
  </div>;
}

const CONSOLE_SCENES = {
  operators: {
    nav: "Chat",
    question: "Which checks come before a release?",
    answer: "Start with the release checklist, then confirm rollback owner and the customer notice window.",
    source: "Release playbook.pdf",
    meta: "3 sources · cited",
  },
  developers: {
    nav: "API",
    question: "POST /api/v1/chat/query",
    answer: "Query the same indexed knowledge from the apps you already ship. The reply still returns the file.",
    source: "workspace API key",
    meta: "Bearer · cited",
  },
  admins: {
    nav: "Access",
    question: "Who can query this knowledge base?",
    answer: "Owners and editors can index. Viewers can ask. Keys are issued and revoked in the same workspace.",
    source: "Workspace roles",
    meta: "recorded",
  },
};

export function ProductConsole({ scene = "operators" }) {
  const view = CONSOLE_SCENES[scene] || CONSOLE_SCENES.operators;
  return (
    <div className="wc-console">
      <div className="wc-console-chrome">
        <span className="wc-console-dots" aria-hidden="true"><i/><i/><i/></span>
        <span className="wc-console-url">copilot.wecrew.in/app/chat</span>
        <span className="wc-console-live">Live workspace</span>
      </div>
      <div className="wc-console-body">
        <aside className="wc-console-rail">
          <p className="wc-console-brand">WeCrew Copilot</p>
          {["Chat", "Library", "Access", "API"].map((item) => (
            <span key={item} className={item === view.nav ? "is-active" : ""}>{item}</span>
          ))}
        </aside>
        <div className="wc-console-main">
          <p className="wc-console-q">{view.question}</p>
          <div className="wc-console-a">
            <p>{view.answer}</p>
            <span className="wc-source-chip">{view.source} · {view.meta}</span>
          </div>
        </div>
        <aside className="wc-console-sources">
          <p>Sources</p>
          <span>Release playbook.pdf</span>
          <span>Support handbook.md</span>
          <span>Change window.docx</span>
        </aside>
      </div>
    </div>
  );
}
